import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ShieldCheck, LogIn, LogOut, Plus, Wrench, AlertTriangle, CheckCircle2, Trash2 } from "lucide-react";
import { rank } from "../../lib/rbac";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Security() {
  const [tab, setTab] = useState("logs");
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ person_name: "", person_type: "Visitor", direction: "entry", purpose: "", vehicle_no: "" });

  const [checklist, setChecklist] = useState([]);
  const [equipItems, setEquipItems] = useState([]);
  const [safetyNotes, setSafetyNotes] = useState("");
  const [checks, setChecks] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [canEdit, setCanEdit] = useState(false);

  const fetchLogs = useCallback(async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/security/logs`); setLogs(r.data); } catch (e) { console.error(e); }
  }, []);

  const fetchEquip = useCallback(async () => {
    try {
      const [cl, ch, me] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/security/checklist`),
        axios.get(`${BACKEND_URL}/api/security/equipment-checks`),
        axios.get(`${BACKEND_URL}/api/auth/me`),
      ]);
      const list = (cl.data || []).filter((x) => x.active !== false);
      setChecklist(list);
      setEquipItems(list.map((c) => ({ name: c.name, status: "OK", note: "" })));
      setChecks(ch.data);
      setCanEdit(rank(me.data.user.role) >= 60);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchLogs(); fetchEquip(); }, [fetchLogs, fetchEquip]);

  const addChecklistItem = async (e) => {
    e.preventDefault();
    if (!newItem.trim()) return;
    try { await axios.post(`${BACKEND_URL}/api/security/checklist`, { name: newItem.trim() }); setNewItem(""); fetchEquip(); }
    catch (err) { alert(err.response?.data?.detail || "Only Branch Manager and above can edit the checklist."); }
  };
  const delChecklistItem = async (id) => {
    try { await axios.delete(`${BACKEND_URL}/api/security/checklist/${id}`); fetchEquip(); }
    catch (err) { alert(err.response?.data?.detail || "Only Branch Manager and above can edit the checklist."); }
  };

  const addLog = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/security/logs`, form);
      setForm({ person_name: "", person_type: "Visitor", direction: "entry", purpose: "", vehicle_no: "" });
      fetchLogs();
    } catch (e) { alert(e.response?.data?.detail || "Could not log entry"); }
  };

  const toggleStatus = (i) => setEquipItems((p) => p.map((it, idx) => idx === i ? { ...it, status: it.status === "OK" ? "Issue" : "OK" } : it));
  const setNote = (i, v) => setEquipItems((p) => p.map((it, idx) => idx === i ? { ...it, note: v } : it));

  const submitCheck = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/security/equipment-checks`, { items: equipItems, safety_notes: safetyNotes });
      setSafetyNotes("");
      fetchEquip();
      alert("Equipment & safety check submitted.");
    } catch (e) { alert(e.response?.data?.detail || "Could not submit"); }
  };

  const inp = "w-full bg-card_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm";

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700" data-testid="security-page">
      <div>
        <h1 className="text-4xl font-serif text-text_primary mb-3">Security</h1>
        <p className="text-text_secondary text-sm">Log entry & exit, and verify equipment and safety for your branch.</p>
      </div>

      <div className="flex gap-2">
        <button data-testid="sec-tab-logs" onClick={() => setTab("logs")} className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab === "logs" ? "bg-primary text-[#111827]" : "bg-card_bg border border-border_soft text-text_secondary"}`}>Entry / Exit</button>
        <button data-testid="sec-tab-equip" onClick={() => setTab("equip")} className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors ${tab === "equip" ? "bg-primary text-[#111827]" : "bg-card_bg border border-border_soft text-text_secondary"}`}>Equipment & Safety</button>
      </div>

      {tab === "logs" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <form onSubmit={addLog} className="bg-card_bg border border-border_soft rounded-2xl p-6 space-y-4 lg:col-span-1">
            <h2 className="text-lg font-bold text-text_primary flex items-center gap-2 mb-2"><ShieldCheck size={18} className="text-gold" /> New Log</h2>
            <input required data-testid="sec-person-name" placeholder="Person name" value={form.person_name} onChange={e => setForm({ ...form, person_name: e.target.value })} className={inp} />
            <select data-testid="sec-person-type" value={form.person_type} onChange={e => setForm({ ...form, person_type: e.target.value })} className={inp}>{["Visitor", "Member", "Staff", "Vendor"].map(x => <option key={x}>{x}</option>)}</select>
            <div className="flex gap-2">
              <button type="button" onClick={() => setForm({ ...form, direction: "entry" })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 ${form.direction === "entry" ? "bg-accent_success/20 border border-accent_success text-accent_success" : "bg-main_bg border border-border_soft text-text_secondary"}`}><LogIn size={15} /> Entry</button>
              <button type="button" onClick={() => setForm({ ...form, direction: "exit" })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 ${form.direction === "exit" ? "bg-primary/20 border border-primary text-gold" : "bg-main_bg border border-border_soft text-text_secondary"}`}><LogOut size={15} /> Exit</button>
            </div>
            <input placeholder="Purpose" value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} className={inp} />
            <input placeholder="Vehicle No. (optional)" value={form.vehicle_no} onChange={e => setForm({ ...form, vehicle_no: e.target.value })} className={inp} />
            <button type="submit" data-testid="sec-add-log-btn" className="w-full bg-primary text-[#111827] font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-primary_hover transition-colors"><Plus size={16} /> Add Log</button>
          </form>

          <div className="bg-card_bg border border-border_soft rounded-2xl overflow-hidden lg:col-span-2">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border_soft/50"><tr>
                <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Time</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Person</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Direction</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-widest text-text_secondary font-bold">Purpose</th>
              </tr></thead>
              <tbody className="divide-y divide-border_soft/40">
                {logs.length === 0 && <tr><td colSpan="4" className="px-5 py-8 text-text_secondary text-center">No entry/exit logs today.</td></tr>}
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td className="px-5 py-3 text-text_secondary">{l.time}</td>
                    <td className="px-5 py-3 text-text_primary font-semibold">{l.person_name} <span className="text-text_secondary text-xs">({l.person_type})</span></td>
                    <td className="px-5 py-3"><span className={l.direction === "entry" ? "text-accent_success font-bold" : "text-gold font-bold"}>{l.direction === "entry" ? "IN" : "OUT"}</span></td>
                    <td className="px-5 py-3 text-text_secondary">{l.purpose || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "equip" && (
        <div className="space-y-6">
          {canEdit && (
            <div className="bg-card_bg border border-border_soft rounded-2xl p-6" data-testid="security-checklist-editor">
              <h2 className="text-lg font-bold text-text_primary flex items-center gap-2 mb-1"><ShieldCheck size={18} className="text-gold" /> Security Checklist (master list)</h2>
              <p className="text-text_secondary text-sm mb-4">Items here appear in the daily equipment & safety verification below.</p>
              <div className="space-y-2 mb-4">
                {checklist.map((c) => (
                  <div key={c.id} className="flex justify-between items-center bg-main_bg border border-border_soft/60 rounded-lg px-4 py-2.5">
                    <span className="text-sm text-text_primary">{c.name}</span>
                    <button data-testid={`del-sec-item-${c.id}`} onClick={() => delChecklistItem(c.id)} className="text-text_secondary hover:text-accent_danger"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
              <form onSubmit={addChecklistItem} className="flex gap-2">
                <input data-testid="new-sec-item-input" value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="Add a checklist item..." className="flex-1 bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm" />
                <button data-testid="add-sec-item-btn" type="submit" className="bg-primary text-[#111827] px-5 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-primary_hover transition-colors"><Plus size={16} /> Add</button>
              </form>
            </div>
          )}

          <div className="bg-card_bg border border-border_soft rounded-2xl p-6">
            <h2 className="text-lg font-bold text-text_primary flex items-center gap-2 mb-4"><Wrench size={18} className="text-gold" /> Equipment & Safety Verification</h2>
            <div className="space-y-3">
              {equipItems.map((it, i) => (
                <div key={i} className="flex flex-col md:flex-row md:items-center gap-3 border-b border-border_soft/40 pb-3">
                  <span className="text-text_primary text-sm font-semibold flex-1">{it.name}</span>
                  <button data-testid={`equip-toggle-${i}`} onClick={() => toggleStatus(i)} className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1 ${it.status === "OK" ? "bg-accent_success/20 text-accent_success border border-accent_success/40" : "bg-accent_danger/20 text-accent_danger border border-accent_danger/40"}`}>
                    {it.status === "OK" ? <><CheckCircle2 size={14} /> OK</> : <><AlertTriangle size={14} /> Issue</>}
                  </button>
                  {it.status === "Issue" && <input placeholder="Describe issue" value={it.note} onChange={e => setNote(i, e.target.value)} className="bg-main_bg border border-border_soft rounded-lg p-2 text-text_primary text-sm md:w-64" />}
                </div>
              ))}
            </div>
            <textarea placeholder="Safety notes / observations" value={safetyNotes} onChange={e => setSafetyNotes(e.target.value)} className="w-full bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm mt-4" rows={3} />
            <button data-testid="submit-equip-check-btn" onClick={submitCheck} className="mt-4 bg-primary text-[#111827] font-bold py-3 px-6 rounded-xl hover:bg-primary_hover transition-colors">Submit Check</button>
          </div>

          <div className="bg-card_bg border border-border_soft rounded-2xl p-6">
            <h3 className="text-sm font-bold text-text_primary mb-3">Recent Checks</h3>
            {checks.length === 0 && <p className="text-text_secondary text-sm">No checks submitted yet.</p>}
            {checks.map((c) => (
              <div key={c.id} className="flex items-center justify-between border-b border-border_soft/40 py-2 text-sm">
                <span className="text-text_secondary">{c.date} {c.time} · {c.checked_by}</span>
                <span className={c.issues_count > 0 ? "text-accent_danger font-bold" : "text-accent_success font-bold"}>{c.issues_count > 0 ? `${c.issues_count} issue(s)` : "All OK"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
